export type JPBReferenceProperty = {
  documentID: string;
  dokumentId?: string | null;
  bfe_nr: string;
  matchedBfeNr?: string | null;
  area: number;
  /** Raw transaction price from the deed (not index regulated). */
  originalTotalPrice: number;
  totalPrice: number;
  transactionDate: Date;
  pricePerSquareMeter: number;
  originalPricePerSquareMeter: number;
  kommuneCode?: string | null;
  coordinates: { lat: number; lng: number };
  geoshape: any | null;
  address: string;
  transactionType: string;
  transactionStatus: string;
  landbrugsnotering?: string | null;
  error: number;
  reasonCode?: string | null;
  reasonText?: string | null;
  prisDriftsbygning: number | null;
  prisStuehus: number | null;
  /**
   * Index-regulated building prices (same index method as totalPrice).
   * These are used when subtracting buildings from the indexed transaction price.
   */
  prisDriftsbygningIndexed?: number | null;
  prisStuehusIndexed?: number | null;
  overlapGeometry?: {
    farmlandGeometry: any | null;
    jordbundskortGeometry: any | null;
  };
  overlapArea?: {
    markOverlap: number | null;
    markOverlapLayerYear?: number | null;
    markOverlapCalculatedAt?: number | null;
    jordbundskortOverlap?: { [soilType: string]: number } | null;
    jordbundskortCalculatedAt?: number | null;
  };
  priceIndexSource?: JPBPriceIndexSource;
  indexDifferencePct?: number | null;
};

export type JPBState = {
  referenceProperties: JPBReferenceProperty[];
};

export function initialJPBState(): JPBState {
  return {
    referenceProperties: [],
  };
}

export enum YearGrouping {
  OneYear = 1,
  TwoYears = 2,
  ThreeYears = 3,
  FourYears = 4,
  FiveYears = 5,
}

export enum AreaUnit {
  SquareMeter = "m²",
  Hectare = "ha",
}

export enum PriceMode {
  Standard = "Standard",
  Landbrug = "Landbrug",
}

export enum JPBPriceIndexSource {
  AgroCura = "agrocura",
  Molbak = "molbak",
  None = "none",
}

export enum AverageCalculationMethod {
  WeightedByArea = "WeightedByArea",
  EqualPerSample = "EqualPerSample",
}

export enum SoilQualityGroup {
  JB_1_2 = "JB 1-2",
  JB_3_4 = "JB 3-4",
  JB_5_6 = "JB 5-6",
  JB_7_10 = "JB 7-10",
  JB_11 = "JB 11",
}

export const SOIL_QUALITY_GROUPS = [
  SoilQualityGroup.JB_1_2,
  SoilQualityGroup.JB_3_4,
  SoilQualityGroup.JB_5_6,
  SoilQualityGroup.JB_7_10,
  SoilQualityGroup.JB_11,
] as const;

/**
 * Expert-defined soil quality indices (normalized so JB 5-6 = 1.0)
 * These represent relative productivity/value, analogous to agriculture vs non-agriculture indices
 */
export const SOIL_INDEX: { [soilGroup in SoilQualityGroup]: number } = {
  [SoilQualityGroup.JB_1_2]: 0.75,
  [SoilQualityGroup.JB_3_4]: 0.925,
  [SoilQualityGroup.JB_5_6]: 1.0,
  [SoilQualityGroup.JB_7_10]: 0.85,
  [SoilQualityGroup.JB_11]: 0.6,
};

export const SOIL_TYPE_COLORS: { [soilType: string]: string } = {
  "Grovsandet jord": "rgb(255, 240, 166)",
  "Finsandet jord": "rgb(255, 206, 181)",
  "Grov lerblandet sandjord": "rgb(252, 187, 96)",
  "Fin lerblandet sandjord": "rgb(250, 168, 15)",
  "Grov sandblandet lerjord": "rgb(201, 156, 87)",
  "Fin sandblandet lerjord": "rgb(161, 106, 3)",
  Lerjord: "rgb(130, 85, 17)",
  "Svær lerjord": "rgb(91, 150, 54)",
  "Meget svær lerjord": "rgb(76, 99, 0)",
  siltjord: "rgb(74, 74, 6)",
  Humusjord: "rgb(200, 230, 80)",
};

export function getSoilTypeColor(jordtype: string | undefined | null): string {
  if (!jordtype) {
    return "rgb(128, 128, 128)";
  }
  return SOIL_TYPE_COLORS[jordtype] || "rgb(128, 128, 128)";
}

export type StatisticsMethod = "quantile" | "mean" | "meanInterval";

export interface JPBAssumptions {
  pricePerMeterMin: number;
  pricePerMeterMax: number;
  areaMin: number;
  areaMax: number;
  includeTransactionTypes: string[];
  effectiveMarkOverlapThreshold: number;
  agricultureFactor: number;
  nonAgricultureFactor: number;
  soilQualityFactors: {
    JB_1_2: number;
    JB_3_4: number;
    JB_5_6: number;
    JB_7_10: number;
    JB_11: number;
  };
  buildingAreaOkM2: number;
  buildingAreaOkUseAgriculturalArea: boolean;
  excludeBuildings: boolean;
}

export const DEFAULT_ASSUMPTIONS: JPBAssumptions = {
  pricePerMeterMin: 10,
  pricePerMeterMax: 50,
  areaMin: 50000,
  areaMax: 1000000,
  includeTransactionTypes: ["almindeligFriHandel"],
  effectiveMarkOverlapThreshold: 25,
  agricultureFactor: 1.0,
  nonAgricultureFactor: 0.5,
  soilQualityFactors: {
    JB_1_2: 0.75,
    JB_3_4: 0.925,
    JB_5_6: 1.0,
    JB_7_10: 0.85,
    JB_11: 0.6,
  },
  buildingAreaOkM2: 50,
  buildingAreaOkUseAgriculturalArea: false,
  excludeBuildings: false,
};

// MARK: Report types

/**
 * Subject polygon analysis result emitted by `JPBPolygonAnalyzer.onAnalysisChange`
 * and consumed by the report generator. Only the fields actually used by the
 * report (subject address/geometry, the headline price, and the polygon area)
 * are kept here — everything else is rendered from a live snapshot of the
 * polygon-analyzer card itself.
 */
export type JPBReportPolygonAnalysis = {
  subjectAddress?: string;
  /** GeoJSON geometry of the analyzed polygon (used for the subject-map snapshot). */
  polygonGeoJson?: any;
  polygonAreaM2: number;
  /** Final estimated total kr/m² for the polygon — the report's headline result. */
  estimatedPricePerSquareMeter: number;
};

export type JPBReportData = {
  yearGrouping: YearGrouping;
  averageCalculationMethod?: AverageCalculationMethod;
  assumptions: JPBAssumptions;
  referenceProperties: JPBReferenceProperty[];
  checkedDocumentIds: string[];
  calculationDateFrom?: Date;
  calculationDateTo?: Date;
  overviewWithJordbundImage?: Uint8Array;
  histogramImage?: Uint8Array;
  /** Search radius in km used for the buffer around the subject. */
  searchRadiusKm?: number;
  /** The user's actual buffer distance in km applied around the source layer. */
  bufferDistanceKm?: number;
  /** Total area (km²) of the search region (source layer + buffer). */
  searchAreaKm2?: number;
  /** Display name of the source layer that defines the analysed area. */
  sourceLayerName?: string;
  /** Geometric kind of the source layer used to define the analysed area. */
  sourceLayerGeometryKind?:
  | "point"
  | "multipoint"
  | "line"
  | "polygon"
  | "multipolygon"
  | "mixed";
  /** Number of features in the source layer. */
  sourceFeatureCount?: number;
  /** Whether index regulation is currently applied (`priceIndexSource !== None`). */
  applyIndexCalculation?: boolean;
  /** Resolved index source label, e.g. "Mølbaks Jordprisindeks". */
  priceIndexSourceLabel?: string;
  /** Polygon-analyzer result for the subject area; required by the report. */
  polygonAnalysis?: JPBReportPolygonAnalysis;
  /** Close-up of the subject polygon. */
  subjectPolygonImage?: Uint8Array;
  /** Comparison map showing ALL points (no checked-only filter). */
  allPointsMapImage?: Uint8Array;
  /** Heatmap with price-colored circles sized by area + legend (Bilag 3). */
  heatmapImage?: Uint8Array;
  /** Snapshot of the JPB statistics card (with soil rows expanded). */
  statisticsCardImage?: Uint8Array;
  /** Snapshot of the entire JPB polygon analyzer card. */
  polygonAnalyzerImage?: Uint8Array;
  /** Kommune name of the subject polygon. */
  subjectKommune?: string;
  /** BFE-nr. of the subject ejendom. */
  subjectBfe?: string;
  /** "<matrikelnr>, <ejerlavsnavn>" formatted string. */
  subjectMatr?: string;
  /** Subject polygon area in m² (mirrors `polygonAnalysis.polygonAreaM2` for convenience). */
  subjectAreaM2?: number;
  /** Email of the user generating the report. */
  userEmail?: string;
  /** Generation timestamp; defaults to `new Date()` in the generator. */
  reportDate?: Date;
};
